import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, updateDoc, Timestamp, collection, getDocs, query, orderBy, setDoc, deleteDoc } from 'firebase/firestore';
import { FileStorageService } from '../../services/FileStorageService';
import { db as firestore } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { UserManagementService } from '../../services/UserManagementService';
import {
  StoreInfoCard,
  NotesSection,
  NotesList
} from '../../components/store';
import { Header, OffcanvasMenu, Lightbox, TabbedInterface } from '../../components/common';
import { OptimizedHeritageOrderForm } from '../../components/forms/OptimizedHeritageOrderForm';
import { OptimizedLivingstonOrderForm } from '../../components/forms/OptimizedLivingstonOrderForm';
import { OptimizedFlowerOrderForm } from '../../components/forms/OptimizedFlowerOrderForm';
import { OptimizedVegetableOrderForm } from '../../components/forms/OptimizedVegetableOrderForm';
import { StoreOrderSuppliesForm } from '../../components/forms/StoreOrderSuppliesForm';
import { PlanogramOrderForm } from '../../components/forms/PlanogramOrderForm';
import { GlobalOrderProvider, useGlobalOrderData } from '../../hooks/useGlobalOrderData';
import { GlobalOrderSummary } from '../../components/forms/shared/GlobalOrderSummary';
import { PreviewOrderButton } from '../../components/forms/shared/PreviewOrderButton';
import { CustomerInformationSection, OrderInformationSection } from '../../components/forms/shared/SharedFormSections';
import { useCustomerData } from '../../components/forms/shared/CustomerDataService';
import { OrderPreviewScreen } from '../../components/forms/shared/OrderPreviewScreen';
import { OrderSuppliesPreviewScreen } from '../../components/forms/shared/OrderSuppliesPreviewScreen';
import { HistoricalOrderPreviewScreen } from '../../components/forms/shared/HistoricalOrderPreviewScreen';
import { StoreHistory } from '../../components/common/StoreHistory';
import { ResetOrderButton } from '../../components/forms/shared/ResetOrderButton';
import { Store, Note, Media, Document } from '../../types/storeTypes';
import { 
  PageContainer, 
  MainContent,
  Card,
  FlexColumn,
  Title,
  Text,
  Button,
  Spacer
} from '../../styles/StyledComponents';
import styled from 'styled-components';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';
import { Store as StoreIcon, FileText, Package, Flower, Carrot, ShoppingCart, History, Package2, LayoutGrid } from 'lucide-react';

const STORE_DETAIL_TAB_SLUGS: readonly string[] = ['store-visit', 'store-order', 'store-history', 'order-supplies'];

// Custom styled components for mobile optimization
const MobileMainContent = styled(MainContent)`
  padding: ${Spacing.md};
  padding-bottom: ${Spacing.xxl};
`;

const MobileTitle = styled(Title)`
  font-size: ${Typography.fontSize.xl};
  margin-bottom: ${Spacing.md};
`;

const MobileText = styled(Text)`
  font-size: ${Typography.fontSize.md};
`;

const MobileCard = styled(Card)`
  padding: ${Spacing.lg};
  margin-bottom: ${Spacing.md};
`;

interface StoreDetailsLocationState {
  storeId?: string;
  storeBreadcrumbLabel?: string;
  customerNumber?: string;
  [key: string]: unknown;
}

export const StoreDetailsScreenMobile: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const allowPreviewPersistenceRef = useRef(true);
  const tabChangeFromUiRef = useRef(false);

  const [store, setStore] = useState<Store | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(true);
  const [userFullName, setUserFullName] = useState<string>('');

  // Helper function to get localStorage key for this store
  const getStorageKey = (key: string) => `store_${storeId}_${key}`;
  
  // Load persisted state from localStorage on mount
  const loadPersistedState = () => {
    if (!storeId) return { showPreview: false, activeTab: 'store-visit', selectedHistoricalOrderId: null };
    
    // Check for tab query parameter first (overrides persisted state)
    const tabParam = searchParams.get('tab');
    if (tabParam && STORE_DETAIL_TAB_SLUGS.includes(tabParam)) {
      return {
        showPreview: false,
        activeTab: tabParam,
        selectedHistoricalOrderId: null
      };
    }
    
    try {
      const persistedShowPreview = localStorage.getItem(getStorageKey('showPreview'));
      const persistedActiveTab = localStorage.getItem(getStorageKey('activeTab'));
      const persistedHistoricalOrderId = localStorage.getItem(getStorageKey('selectedHistoricalOrderId'));
      
      return {
        showPreview: persistedShowPreview === 'true',
        activeTab: persistedActiveTab || 'store-visit',
        selectedHistoricalOrderId: persistedHistoricalOrderId || null
      };
    } catch (error) {
      console.error('Error loading persisted state:', error);
      return { showPreview: false, activeTab: 'store-visit', selectedHistoricalOrderId: null };
    }
  };
  
  // Save state to localStorage
  const saveState = (key: string, value: any) => {
    if (!storeId) return;
    try {
      localStorage.setItem(getStorageKey(key), String(value));
    } catch (error) {
      console.error(`Error saving state ${key}:`, error);
    }
  };
  
  // Load initial state from localStorage
  const persistedState = loadPersistedState();
  
  const [showPreview, setShowPreview] = useState(persistedState.showPreview);
  const [selectedHistoricalOrderId, setSelectedHistoricalOrderId] = useState<string | null>(persistedState.selectedHistoricalOrderId);
  const [activeTab, setActiveTab] = useState<string>(persistedState.activeTab);
  
  // Effect to save state changes to localStorage
  useEffect(() => {
    if (storeId) {
      saveState('activeTab', activeTab);
    }
  }, [activeTab, storeId]);
  
  useEffect(() => {
    if (!storeId) return;
    if (!allowPreviewPersistenceRef.current && showPreview) {
      return;
    }
    saveState('showPreview', showPreview ? 'true' : 'false');
  }, [showPreview, storeId]);

  useEffect(() => {
    allowPreviewPersistenceRef.current = true;
  }, [storeId]);

  const previewUrlValue = showPreview
    ? activeTab === 'order-supplies'
      ? 'supplies'
      : 'order'
    : null;

  useEffect(() => {
    if (!storeId) return;
    const tabSlug = STORE_DETAIL_TAB_SLUGS.includes(activeTab) ? activeTab : 'store-visit';
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tabSlug);
        if (previewUrlValue) {
          next.set('preview', previewUrlValue);
        } else {
          next.delete('preview');
        }
        return next;
      },
      { replace: true }
    );
  }, [activeTab, previewUrlValue, storeId, setSearchParams]);

  const tabFromUrl = searchParams.get('tab');
  useEffect(() => {
    if (!storeId) return;
    if (!tabFromUrl || !STORE_DETAIL_TAB_SLUGS.includes(tabFromUrl)) {
      return;
    }
    if (tabChangeFromUiRef.current) {
      tabChangeFromUiRef.current = false;
      return;
    }
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
      setShowPreview(false);
      allowPreviewPersistenceRef.current = true;
      saveState('showPreview', 'false');
      saveState('activeTab', tabFromUrl);
    }
  }, [tabFromUrl, activeTab, storeId]);

  useEffect(() => {
    if (storeId) {
      saveState('selectedHistoricalOrderId', selectedHistoricalOrderId || '');
    }
  }, [selectedHistoricalOrderId, storeId]);

  // Preview handlers
  const handleShowPreview = () => {
    allowPreviewPersistenceRef.current = true;
    setShowPreview(true);
    saveState('showPreview', 'true');
    window.scrollTo(0, 0);
    document.documentElement.scrollTo(0, 0);
  };

  const handleBackFromPreview = () => {
    allowPreviewPersistenceRef.current = true;
    setShowPreview(false);
    saveState('showPreview', 'false');
    window.scrollTo(0, 0);
    document.documentElement.scrollTo(0, 0);
  };

  const handleOrderFinalizedSuccess = () => {
    allowPreviewPersistenceRef.current = false;
    saveState('showPreview', 'false');
  };

  const handleExitPreviewToStoreVisit = () => {
    allowPreviewPersistenceRef.current = true;
    setShowPreview(false);
    saveState('showPreview', 'false');
    setActiveTab('store-visit');
    saveState('activeTab', 'store-visit');
    window.scrollTo(0, 0);
    document.documentElement.scrollTo(0, 0);
  };

  const handleFinalSubmit = async (email: string) => {
    // This will be handled by the OrderPreviewScreen component
  };

  // Historical order handlers
  const handleOrderClick = (orderId: string) => {
    setSelectedHistoricalOrderId(orderId);
    saveState('selectedHistoricalOrderId', orderId);
  };

  const handleBackFromHistoricalPreview = () => {
    setSelectedHistoricalOrderId(null);
    saveState('selectedHistoricalOrderId', '');
  };

  // Customer and Order Information Component
  const CustomerAndOrderSections = () => {
    const { customerData, loading: customerLoading, error: customerError } = useCustomerData(storeId || '');
    const { orderData, updateOrderData } = useGlobalOrderData();
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const lastSyncedCustomerRef = React.useRef<string | null>(null);
    
    React.useEffect(() => {
      if (customerData?.storeName && customerData?.customerNumber) {
        const customerKey = `${customerData.storeName}-${customerData.customerNumber}`;
        
        if (lastSyncedCustomerRef.current !== customerKey) {
          const needsSync = !orderData.storeName || 
                           orderData.storeName !== customerData.storeName ||
                           orderData.customerNumber !== customerData.customerNumber;
          
          if (needsSync) {
            lastSyncedCustomerRef.current = customerKey;
            // ONLY sync customer info fields, don't overwrite order info fields (poNumber, specialInstructions, etc.)
            const customerInfo = {
              customerNumber: customerData.customerNumber,
              storeName: customerData.storeName,
              address: customerData.address,
              city: customerData.city,
              province: customerData.province,
              postalCode: customerData.postalCode,
              email: customerData.email,
              phoneNumber: customerData.phoneNumber
            };
            updateOrderData(customerInfo);
          }
        }
      }
    }, [customerData?.storeName, customerData?.customerNumber]);

    const handleInputChange = (field: string, value: string | boolean) => {
      updateOrderData({ [field]: value });
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: '' }));
      }
    };

    return (
      <div>
        <div style={{ marginBottom: Spacing.lg }}>
          <GlobalOrderSummary orderType="store-order" />
          <PreviewOrderButton onPreview={handleShowPreview} orderType="store-order" />
        </div>

        <CustomerInformationSection
          data={orderData}
          onChange={handleInputChange}
          errors={errors}
          loading={customerLoading}
          error={customerError}
        />

        <OrderInformationSection
          data={orderData}
          onChange={handleInputChange}
          errors={errors}
        />
      </div>
    );
  };

  // Helper function to fetch notes with error handling
  const fetchNotesWithFallback = async (storeId: string): Promise<Note[]> => {
    const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
    let notesSnapshot;
    
    try {
      // Try with descending order (requires index)
      const notesQuery = query(notesCollectionRef, orderBy('createdAt', 'desc'));
      notesSnapshot = await getDocs(notesQuery);
    } catch (error: any) {
      // If index error, fetch without orderBy and sort client-side
      if (error?.code === 'failed-precondition' || error?.message?.includes('index')) {
        const indexUrl = error?.message?.match(/https:\/\/[^\s]+/)?.[0] || 
                        'https://console.firebase.google.com/project/erp-mock/firestore/indexes';
        console.warn(`[StoreDetails] Index not ready. Fetching notes without orderBy. Create index: ${indexUrl}`);
        console.error(`%c[StoreDetails] 🔗 Missing Index! Create it here: ${indexUrl}`, 'color: red; font-weight: bold;');
        
        // Fetch without orderBy
        notesSnapshot = await getDocs(notesCollectionRef);
      } else {
        throw error; // Re-throw if it's a different error
      }
    }
    
    const notesArray: Array<{ id: string; data: any; createdAt: any }> = [];
    
    notesSnapshot.forEach((noteDoc) => {
            const noteData = noteDoc.data();
            
            // Ensure competitorData is properly structured
            let competitorData = undefined;
            if (noteData.competitorData && Array.isArray(noteData.competitorData)) {
              competitorData = noteData.competitorData.map((comp: any) => ({
                id: comp.id || '',
                name: comp.name || '',
                photoUrls: Array.isArray(comp.photoUrls) ? comp.photoUrls : (comp.photoUrls ? [comp.photoUrls] : []),
                stockLevel: comp.stockLevel || 0,
                displayPanels: comp.displayPanels !== undefined && comp.displayPanels !== null ? Number(comp.displayPanels) : undefined
              }));
            }
            
            notesArray.push({
              id: noteDoc.id,
              data: {
                text: noteData.text || '',
                date: noteData.date || '',
                time: noteData.time || '',
                author: noteData.author || noteData.managerName || '',
                managerName: noteData.managerName || noteData.author || '',
                visitType: noteData.visitType || undefined,
                competitors: noteData.competitors || [],
                competitorData: competitorData,
                media: noteData.media || [],
                documents: noteData.documents || [],
                nextVisitDate: noteData.nextVisitDate || '',
                replies: noteData.replies || [],
                createdAt: noteData.createdAt,
                storeLevel: noteData.storeLevel,
                lowStockCategories: noteData.lowStockCategories || [],
                requestManagerContact: noteData.requestManagerContact || false
              },
              createdAt: noteData.createdAt
            });
          });
          
    // Sort by createdAt descending (client-side if query didn't have orderBy)
    notesArray.sort((a, b) => {
      const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt));
      const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt));
      return bDate.getTime() - aDate.getTime(); // Descending
    });
    
    // Convert to Note[] format
    const notesData: Note[] = [];
    notesArray.forEach(({ id, data }) => {
      notesData.push({
        id,
        ...data
      });
    });
    
    return notesData;
  };

  // Fetch store data from Firestore
  useEffect(() => {
    const fetchStore = async () => {
      if (!storeId) return;
      
      try {
        setIsLoading(true);
        const storeDocRef = doc(firestore, 'Stores', storeId);
        const storeDocSnap = await getDoc(storeDocRef);
        
        if (storeDocSnap.exists()) {
          const storeData = storeDocSnap.data();
          
          // Fetch notes from the subcollection
          const notesData = await fetchNotesWithFallback(storeId);
          
          setStore({
            id: storeDocSnap.id,
            ...storeData,
            notes: notesData
          } as Store);
        } else {
          setError('Store not found');
        }
      } catch (err) {
        console.error('Error fetching store:', err);
        setError('Failed to load store details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStore();
  }, [storeId]);

  useEffect(() => {
    if (!storeId || !store) {
      return;
    }

    const derivedLabel =
      store.CustomerNo ||
      store.customerNo ||
      store.customerNumber ||
      store.StoreNumber ||
      store.StoreName ||
      '';

    if (!derivedLabel) {
      return;
    }

    const currentState = location.state as StoreDetailsLocationState | null;
    const currentLabel = currentState?.storeBreadcrumbLabel;

    if (currentLabel === String(derivedLabel)) {
      return;
    }

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: {
        ...(currentState || {}),
        storeId,
        storeBreadcrumbLabel: String(derivedLabel),
        customerNumber: String(derivedLabel)
      }
    });
  }, [storeId, store, location.pathname, location.search, location.state, navigate]);

  // Fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id) return;
      
      try {
        const userProfile = await UserManagementService.getUserById(user.id);
        if (userProfile) {
          const fullName = userProfile.firstName && userProfile.lastName
            ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
            : userProfile.displayName || user?.email || '';
          setUserFullName(fullName);
        } else {
          setUserFullName(user?.email || '');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setUserFullName(user?.email || '');
      }
    };

    fetchUserProfile();
  }, [user?.id, user?.email]);

  const handleSubmitNote = async (
    note: string, 
    salesRepName: string,
    selectedCompetitors: string[], 
    attachments: { media: File[], documents: File[] },
    nextVisitDate: string,
    compressionEnabled: boolean = true,
    storeLevel?: number,
    lowStockCategories?: string[],
    requestManagerContact?: boolean,
    competitorData?: { id: string; name: string; photos: File[]; stockLevel: number; displayPanels?: number }[],
    visitType?: 'setup' | 'maintenance' | 'requested' | 'other' | '',
    visitReason?: string,
    onUploadProgress?: (progress: any) => void
  ) => {
    if (!storeId || !store) return;
    
    try {
      const fileStorageService = FileStorageService.getInstance();
      const noteId = `note-${Date.now()}`;
      const uploadedBy = user?.email || 'Unknown';

      const uploadResults = await fileStorageService.uploadAllNoteFiles(
        storeId,
        noteId,
        attachments.media,
        attachments.documents,
        competitorData || [],
        uploadedBy,
        compressionEnabled,
        undefined,
        onUploadProgress
      );

      // Log any upload errors
      if (uploadResults.errors && uploadResults.errors.length > 0) {
        console.warn('⚠️ Some files failed to upload:', uploadResults.errors);
      }

      const uploadedMedia: Media[] = uploadResults.media.map(result => ({
        url: result.url,
        type: 'image' as const,
        filename: result.name
      }));

      const uploadedDocuments: Media[] = uploadResults.documents.map(result => ({
        url: result.url,
        type: 'document' as const,
        filename: result.name
      }));

      // Build competitor data with uploaded photo URLs
      let competitorDataWithUrls: { id: string; name: string; photoUrls: string[]; stockLevel: number; displayPanels?: number }[] = [];
      if (competitorData && competitorData.length > 0) {
        for (const competitor of competitorData) {
          const photoUrls = (uploadResults.competitorPhotos[competitor.id] || []).map(r => r.url);
          competitorDataWithUrls.push({
            id: competitor.id,
            name: competitor.name,
            photoUrls: photoUrls,
            stockLevel: competitor.stockLevel,
            ...(competitor.displayPanels !== undefined && competitor.displayPanels > 0 ? { displayPanels: competitor.displayPanels } : {})
          });
        }
      }

      // Create the new note object
      const now = new Date();
      // Use explicit locale to avoid issues on Android browsers (Samsung Galaxy Chrome, etc.)
      const formattedDate = now.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
      const formattedTime = now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
      
      const newNote: Note = {
        id: noteId,
        text: note || '',
        date: formattedDate,
        time: formattedTime,
        author: user?.email || 'Anonymous',
        managerName: salesRepName || userFullName || user?.email || '',
        salesRepName: salesRepName || userFullName || user?.email || '',
        visitType: visitType || undefined,
        competitors: selectedCompetitors || [],
        // Only include competitorData if it has values (Firestore doesn't allow undefined)
        ...(competitorDataWithUrls.length > 0 ? { competitorData: competitorDataWithUrls } : {}),
        media: uploadedMedia || [],
        documents: uploadedDocuments || [],
        nextVisitDate: nextVisitDate || undefined,
        storeLevel: storeLevel !== undefined && storeLevel !== null ? storeLevel : 100,
        lowStockCategories: lowStockCategories || [],
        requestManagerContact: requestManagerContact || false,
        visitReason: visitReason || undefined,
        createdAt: Timestamp.now()
      };

      // Save note as a document in the notes subcollection
      const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
      const noteDocRef = doc(notesCollectionRef, noteId);
      
      // Remove undefined values - Firestore doesn't allow undefined fields
      const noteToSave: any = {
        ...newNote,
          storeLevel: typeof newNote.storeLevel === 'number' ? newNote.storeLevel : 100
      };
      
      // Remove undefined fields (Firestore doesn't support undefined)
      Object.keys(noteToSave).forEach(key => {
        if (noteToSave[key] === undefined) {
          delete noteToSave[key];
        }
      });
      
      await setDoc(noteDocRef, noteToSave);
      
      // Update nextVisitDate on store document if provided
      const storeDocRef = doc(firestore, 'Stores', storeId);
      
      let nextVisitTimestamp = null;
      if (nextVisitDate) {
        let daysToAdd = 0;
        if (nextVisitDate === 'Next Week') {
          daysToAdd = 7;
        } else if (nextVisitDate === '+2 Weeks') {
          daysToAdd = 14;
        } else if (nextVisitDate === '+3 Weeks') {
          daysToAdd = 21;
        } else if (nextVisitDate === '+4 Weeks') {
          daysToAdd = 28;
        } else if (nextVisitDate === '+5 Weeks') {
          daysToAdd = 35;
        }
        
        if (daysToAdd > 0) {
          const nextVisit = new Date(Date.now() + daysToAdd * 24 * 60 * 60 * 1000);
          nextVisitTimestamp = Timestamp.fromDate(nextVisit);
        }
      }
      
      // Only managers can update the Store document (Firestore rules). Hand-lead can create notes
      // but cannot update Stores, so we skip this to avoid throwing and ensure the note is persisted.
      if (nextVisitTimestamp && (userRole === 'manager')) {
        await updateDoc(storeDocRef, {
          nextVisitDate: nextVisitTimestamp
        });
      }

      // Update denormalized fields (notesCount, lastVisit) for database-level sorting
      const { StoreService } = await import('../../services/StoreService');
      StoreService.updateStoreDenormalizedFields(storeId).catch(err => {
        console.warn('Failed to update denormalized fields:', err);
      });
      
      if (user?.id || user?.email) {
        const { NotificationTriggerService } = await import('../../services/NotificationTriggerService');
        NotificationTriggerService.triggerNoteAddedNotification(
          storeId,
          noteId,
          user?.id || user?.email || 'Unknown',
          store?.StoreName || ''
        ).catch(err => {
          console.error('Failed to trigger note notification:', err);
        });
      }

      // Reload notes from subcollection to get updated data
      const notesData = await fetchNotesWithFallback(storeId);
      
      // Update local state
      if (store) {
        const updatedStore = {
          ...store,
          notes: [newNote, ...(store.notes || [])]
        };
        setStore(updatedStore);
      }
    } catch (err: any) {
      console.error('Error submitting note:', err);
      // Provide more detailed error message for debugging
      const errorMessage = err?.message || err?.toString() || 'Unknown error';
      console.error('Error details:', {
        message: errorMessage,
        stack: err?.stack,
        code: err?.code,
        name: err?.name
      });
      setError(`Failed to submit note: ${errorMessage}`);
      // Re-throw so the caller can handle it
      throw err;
    }
  };

  const handleSubmitReply = async (
    noteId: string,
    text: string,
    media: File[],
    documents: File[],
    compressionEnabled: boolean = true
  ) => {
    if (!storeId || !store) {
      console.error('Cannot submit reply: storeId or store is missing');
      throw new Error('Store information is missing');
    }
    
    try {
      const fileStorageService = FileStorageService.getInstance();
      const replyId = `reply-${Date.now()}`;
      const uploadedBy = user?.email || 'Unknown';

      // Upload files to Firebase Storage
      const uploadResults = await fileStorageService.uploadStoreNoteAttachments(
        storeId,
        replyId,
        media,
        documents,
        uploadedBy,
        compressionEnabled
      );

      // Log any upload errors
      if (uploadResults.errors && uploadResults.errors.length > 0) {
        console.warn('⚠️ Some files failed to upload in reply:', uploadResults.errors);
        const errorMessages = uploadResults.errors.map(e => `${e.fileName}: ${e.error}`).join('; ');
        console.warn('Upload errors:', errorMessages);
      }

      // Convert upload results to Media and Document objects
      const mediaResults: Media[] = uploadResults.media.map(result => ({
        name: result.name,
        type: result.type,
        url: result.url
      }));

      const documentResults: Document[] = uploadResults.documents.map(result => ({
        name: result.name,
        type: result.type,
        url: result.url
      }));

      // Create the reply object
      const now = new Date();
      // Use explicit locale to avoid issues on Android browsers (Samsung Galaxy Chrome, etc.)
      const formattedDate = now.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
      const formattedTime = now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
      
      const newReply: Note = {
        id: replyId,
        text,
        managerName: user?.email || 'Unknown',
        author: user?.email || 'Unknown',
        date: formattedDate,
        time: formattedTime,
        media: mediaResults,
        documents: documentResults,
        createdAt: Timestamp.now()
      };

      // Update the note document in the subcollection with the reply
      const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
      const noteDocRef = doc(notesCollectionRef, noteId);
      const noteDocSnap = await getDoc(noteDocRef);
      
      if (!noteDocSnap.exists()) {
        throw new Error('Note not found');
      }

      const noteData = noteDocSnap.data();
      const existingReplies = (noteData.replies || []) as Note[];
      
      // Add the new reply to the existing replies array
      const updatedReplies = [...existingReplies, newReply];

      // Update the note document in the subcollection
      await updateDoc(noteDocRef, {
        replies: updatedReplies
      });

      // Reload notes from subcollection to get updated data
      const notesData = await fetchNotesWithFallback(storeId);

      // Update local state
      setStore(prevStore => {
        if (!prevStore) return null;
        return {
          ...prevStore,
          notes: notesData
        };
      });

      // Trigger notification for reply to note
      if (user?.id && noteData.author) {
        const { NotificationTriggerService } = await import('../../services/NotificationTriggerService');
        NotificationTriggerService.triggerNoteReplyNotification(
          storeId,
          noteId,
          user.id,
          noteData.author, // Original note author email
          store?.StoreName || store?.name
        ).catch(err => {
          console.error('Failed to trigger reply notification:', err);
          // Don't fail the reply submission if notification fails
        });
      }

      console.log('✅ Reply submitted successfully', { noteId, replyId, repliesCount: updatedReplies.length });
    } catch (err) {
      console.error('❌ Error submitting reply:', err);
      setError('Failed to submit reply');
      throw err;
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!storeId || !store) return;
    
    try {
      // Delete the note document from the subcollection
      const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
      const noteDocRef = doc(notesCollectionRef, noteId);
      await deleteDoc(noteDocRef);
      
      // Reload notes from subcollection to get updated data
      const notesData = await fetchNotesWithFallback(storeId);
      
      // Update local state
      setStore(prevStore => {
        if (!prevStore) return null;
        return {
          ...prevStore,
          notes: notesData
        };
      });
      
      console.log('✅ Note deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting note:', error);
      setError('Failed to delete note. Please try again.');
    }
  };

  const handleDeleteReply = async (noteId: string, replyId: string) => {
    if (!storeId || !store) return;
    
    try {
      const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
      const noteDocRef = doc(notesCollectionRef, noteId);
      const noteDocSnap = await getDoc(noteDocRef);
      
      if (!noteDocSnap.exists()) {
        throw new Error('Note not found');
      }
      
      const noteData = noteDocSnap.data();
      const existingReplies = (noteData.replies || []) as Note[];
      
      // Remove the reply from the array
      const updatedReplies = existingReplies.filter(reply => reply.id !== replyId);
      
      // Update the note document
      await updateDoc(noteDocRef, {
        replies: updatedReplies
      });
      
      // Reload notes from subcollection to get updated data
      const notesData = await fetchNotesWithFallback(storeId);
      
      // Update local state
      setStore(prevStore => {
        if (!prevStore) return null;
        return {
          ...prevStore,
          notes: notesData
        };
      });
      
      console.log('✅ Reply deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting reply:', error);
      setError('Failed to delete reply. Please try again.');
    }
  };

  const handleRefreshNotes = async () => {
    if (!storeId) return;
    const notesData = await fetchNotesWithFallback(storeId);
    setStore(prevStore => {
      if (!prevStore) return null;
      return { ...prevStore, notes: notesData };
    });
  };

  const handleOpenLightbox = (imageUrl: string) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
  };

  const handleToggleMenu = () => {
    setIsMenuCollapsed(!isMenuCollapsed);
  };

  if (isLoading) {
    return (
      <PageContainer isMenuCollapsed={true}>
        <OffcanvasMenu
          isCollapsed={isMenuCollapsed}
          onToggle={handleToggleMenu}
          onNavigate={navigate}
        />
        <Header
          isMenuCollapsed={isMenuCollapsed}
          notificationCount={0}
          onMenuToggle={handleToggleMenu}
        />
        <MobileMainContent>
          <FlexColumn align="center" justify="center" style={{ minHeight: '200px' }}>
            <MobileText size="lg">Loading store details...</MobileText>
          </FlexColumn>
        </MobileMainContent>
      </PageContainer>
    );
  }

  if (error || !store) {
    return (
      <PageContainer isMenuCollapsed={true}>
        <OffcanvasMenu
          isCollapsed={isMenuCollapsed}
          onToggle={handleToggleMenu}
          onNavigate={navigate}
        />
        <Header
          isMenuCollapsed={isMenuCollapsed}
          notificationCount={0}
          onMenuToggle={handleToggleMenu}
        />
        <MobileMainContent>
          <MobileCard>
            <FlexColumn align="center" gap="16px">
              <MobileText color="error" size="lg">{error || 'Store not found'}</MobileText>
              <Button onClick={() => navigate('/manager/store-visit')}>Back to Store Visit</Button>
            </FlexColumn>
          </MobileCard>
        </MobileMainContent>
      </PageContainer>
    );
  }

  // Show historical order preview if an order is selected
  if (selectedHistoricalOrderId && storeId) {
    return (
      <GlobalOrderProvider storeId={storeId}>
        <PageContainer isMenuCollapsed={true}>
          <OffcanvasMenu
            isCollapsed={isMenuCollapsed}
            onToggle={handleToggleMenu}
            onNavigate={navigate}
          />
          <Header
            isMenuCollapsed={isMenuCollapsed}
            notificationCount={0}
            onMenuToggle={handleToggleMenu}
          />
          <MobileMainContent>
            <HistoricalOrderPreviewScreen
              storeId={storeId}
              orderId={selectedHistoricalOrderId}
              onBack={handleBackFromHistoricalPreview}
              storeName={store.StoreName}
              onNavigate={(tab) => {
                setSelectedHistoricalOrderId(null);
                saveState('selectedHistoricalOrderId', '');
                if (tab === 'store-history') {
                  setActiveTab('store-history');
                  saveState('activeTab', 'store-history');
                } else if (tab === 'store-visit') {
                  setActiveTab('store-visit');
                  saveState('activeTab', 'store-visit');
                } else if (tab === 'store-order') {
                  setActiveTab('store-order');
                  saveState('activeTab', 'store-order');
                }
              }}
            />
          </MobileMainContent>
        </PageContainer>
      </GlobalOrderProvider>
    );
  }

  // Show preview screen if preview is active
  if (showPreview) {
    return (
      <GlobalOrderProvider storeId={storeId || ''}>
        <PageContainer isMenuCollapsed={true}>
          <OffcanvasMenu
            isCollapsed={isMenuCollapsed}
            onToggle={handleToggleMenu}
            onNavigate={navigate}
          />
          <Header
            isMenuCollapsed={isMenuCollapsed}
            notificationCount={0}
            onMenuToggle={handleToggleMenu}
          />
          <MobileMainContent>
            {activeTab === 'order-supplies' ? (
              <OrderSuppliesPreviewScreen 
                onBack={handleBackFromPreview}
                onFinalSubmit={handleFinalSubmit}
                storeId={storeId}
                storeName={store.StoreName}
                onOrderFinalizedSuccess={handleOrderFinalizedSuccess}
                onExitPreviewToStoreVisit={handleExitPreviewToStoreVisit}
                onNavigate={(tab) => {
                  if (tab === 'store-visit') {
                    setShowPreview(false);
                  } else if (tab === 'store-history') {
                    setShowPreview(false);
                  }
                }}
              />
            ) : (
              <OrderPreviewScreen 
                onBack={handleBackFromPreview}
                onFinalSubmit={handleFinalSubmit}
                storeId={storeId}
                storeName={store.StoreName}
                orderType="store-order"
                onOrderFinalizedSuccess={handleOrderFinalizedSuccess}
                onExitPreviewToStoreVisit={handleExitPreviewToStoreVisit}
                onNavigate={(tab) => {
                  if (tab === 'store-visit') {
                    setShowPreview(false);
                  } else if (tab === 'store-history') {
                    setShowPreview(false);
                  }
                }}
              />
            )}
          </MobileMainContent>
        </PageContainer>
      </GlobalOrderProvider>
    );
  }

  return (
    <GlobalOrderProvider storeId={storeId || ''}>
      <PageContainer isMenuCollapsed={true}>
        <OffcanvasMenu
          isCollapsed={isMenuCollapsed}
          onToggle={handleToggleMenu}
          onNavigate={navigate}
        />
        <Header
          isMenuCollapsed={isMenuCollapsed}
          notificationCount={0}
          onMenuToggle={handleToggleMenu}
        />
        <MobileMainContent>
          <FlexColumn gap="24px">
            <TabbedInterface
              variant="mobile"
              middleSection={<CustomerAndOrderSections />}
              showMiddleSectionForTabs={['store-order']}
              defaultActiveTab="store-visit"
              activeTab={activeTab}
              onTabChange={(tab) => {
                tabChangeFromUiRef.current = true;
                setActiveTab(tab);
                saveState('activeTab', tab);
                setShowPreview(false);
                saveState('showPreview', 'false');
              }}
              tabs={[
                {
                  id: 'store-visit',
                  label: t('navigation.storeVisit', 'Store Visit'),
                  icon: StoreIcon,
                  content: (
                    <FlexColumn gap="24px">
                      <StoreInfoCard 
                        customerNumber={store.CustomerNo || store.customerNo || store.customerNumber || ''}
                        storeName={store.StoreName || ''}
                        address={store.Address || ''}
                        cityTown={store.City || ''}
                        storeNumber={store.StoreNumber || ''}
                        province={store.Prov || store.Province || ''}
                        postalCode={store.PostalCode || ''}
                        sortField={store.SortField || ''}
                        phoneNumber={store.Telephone || store.Phone || ''}
                        salesGroup={store.SalesGroup || ''}
                        email={store.EmailAddress || store.EmailAdddress || store.Email || ''}
                      />
                      
                      <NotesSection 
                        onSubmitNote={(note, salesRepName, competitors, attachments, nextVisitDate, compressionEnabled = true, storeLevel, lowStockCategories, requestManagerContact, competitorData, visitType, visitReason, onUploadProgress) => {
                          return handleSubmitNote(
                            note,
                            salesRepName,
                            competitors,
                            attachments,
                            nextVisitDate,
                            compressionEnabled,
                            storeLevel,
                            lowStockCategories,
                            requestManagerContact,
                            competitorData,
                            visitType,
                            visitReason,
                            onUploadProgress
                          );
                        }}
                        defaultSalesRepName={userFullName || user?.email || ''}
                        storeId={storeId}
                      />
                      
                      <NotesList 
                        notes={store.notes || []} 
                        onViewMediaFullScreen={(media) => {
                          const imageUrl = media.dataUrl || media.url;
                          if (imageUrl) {
                            handleOpenLightbox(imageUrl);
                          }
                        }}
                        onSubmitReply={handleSubmitReply}
                        onDeleteNote={handleDeleteNote}
                        onDeleteReply={handleDeleteReply}
                        onRefreshNotes={handleRefreshNotes}
                        isManager={user?.role === 'manager' || userRole === 'manager'}
                        currentUserEmail={user?.email}
                      />
                    </FlexColumn>
                  )
                },
                {
                  id: 'store-order',
                  label: 'Store Order',
                  icon: ShoppingCart,
                  subTabs: [
                    {
                      id: 'vegetable-order',
                      label: 'Vegetable Order',
                      icon: Carrot,
                      content: <OptimizedVegetableOrderForm storeId={storeId} onShowPreview={handleShowPreview} />
                    },
                    {
                      id: 'flower-order',
                      label: 'Flower Order',
                      icon: Flower,
                      content: <OptimizedFlowerOrderForm storeId={storeId} onShowPreview={handleShowPreview} />
                    },
                    {
                      id: 'heritage-order',
                      label: 'Heritage/Seedtape/Organic',
                      icon: FileText,
                      content: <OptimizedHeritageOrderForm storeId={storeId} onShowPreview={handleShowPreview} />
                    },
                    {
                      id: 'livingston-order',
                      label: 'Livingston Order',
                      icon: Package,
                      content: <OptimizedLivingstonOrderForm storeId={storeId} onShowPreview={handleShowPreview} />
                    },
                    {
                      id: 'planogram-order',
                      label: 'Planogram Order',
                      icon: LayoutGrid,
                      content: <PlanogramOrderForm storeId={storeId} onShowPreview={handleShowPreview} />
                    }
                  ]
                },
                {
                  id: 'store-history',
                  label: 'Order History',
                  icon: History,
                  content: (
                    <StoreHistory 
                      storeId={storeId || ''} 
                      storeName={store.StoreName}
                      onOrderClick={handleOrderClick}
                    />
                  )
                },
                {
                  id: 'order-supplies',
                  label: 'Order Supplies',
                  icon: Package2,
                  content: (
                    <div>
                      <div style={{ marginBottom: Spacing.xl }}>
                        <Text style={{ color: ThemeColors.text.secondary, marginBottom: Spacing.lg }}>
                          Order promotional materials and seed displays for this store
                        </Text>
                      </div>
                      <StoreOrderSuppliesForm storeId={storeId} onShowPreview={handleShowPreview} />
                    </div>
                  )
                }
              ]}
            />
          </FlexColumn>
        </MobileMainContent>
      </PageContainer>
      <Lightbox
        isOpen={lightboxOpen}
        imageUrl={lightboxImage}
        onClose={handleCloseLightbox}
      />
    </GlobalOrderProvider>
  );
};